import { App, AppDatabaseUsage, AuthenticationError, AuthorizationError, ContainerDiskSize, ContainerStats, NetworkError, Subscription } from '@back4app2/sdk';
import { useOutletContext } from 'react-router-dom';
import { useEffect, useReducer, useState } from 'react';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import back4app2 from '../back4app2';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import { createRandomColor, getContainerStatsGraphData } from '../utils/metrics';
import { MetricChart, ScreenHead, SettingsSection } from '../components/ds';

// Both subscriptions ask for the same window, and every card says so in its
// eyebrow — so the number and the words for it live in one place.
const STATS_WINDOW = 3 * 60 * 60 * 1000;
const STATS_WINDOW_LABEL = 'last 3h';

// Database storage is the one card here that is not live. It is written by the
// daily sweep (`measureAppDatabasesStorage`), one point per day, so it gets its
// own window and says so in its eyebrow like the rest of them — a month is what
// makes growth visible, and there is nothing faster to show.
const STORAGE_DAYS = 30;
const STORAGE_WINDOW_LABEL = 'last 30d';

// Its axis is days, so the clock the other cards use would print 00:00 three
// times. UTC because that is the hour the sweep stamps the day at; rendering it
// locally moves half the world's points to the previous day.
const dayLabel = (ms: number) =>
  new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'UTC' });

// The series carry raw units — bytes for memory and disk, bytes per second for
// the network — so every card states what it is showing rather than printing
// the number it was handed.
const formatMB = (bytes: number) => `${Math.round(bytes / (1024 * 1024))}`;

const formatBytes = (bytes: number) => {
  const units = ['B', 'kB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const formatRate = (bytesPerSecond: number) => `${formatBytes(bytesPerSecond)}/s`;

interface MyAppMetricsState {
  isLoadingDisksSizes: boolean;
  loadingDisksSizesErrorMessage?: string;
  containerDiskSizeGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max?: number };
  diskSizeGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
  isLoadingStats: boolean;
  loadingStatsErrorMessage?: string;
  colors?: { [containerId: string]: string };
  cpuGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
  ramGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max: number};
  pidGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max: number};
  netInGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
  netOutGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
}

const INITIAL_STATE: MyAppMetricsState = {
  isLoadingDisksSizes: true,
  isLoadingStats: true
};

enum MyAppMetricsActionType {
  RESET,
  FINISH_LOADING_DISKS_SIZES,
  FINISH_LOADING_STATS
}

const reset = () => ({
  type: MyAppMetricsActionType.RESET
} as const);

const finishLoadingDisksSizes = (errorMessage?: string, disksSizes?: ContainerDiskSize[]) => ({
  type: MyAppMetricsActionType.FINISH_LOADING_DISKS_SIZES,
  payload: {
    errorMessage,
    disksSizes
  }
} as const);

const finishLoadingStats = (errorMessage?: string, stats?: ContainerStats[]) => ({
  type: MyAppMetricsActionType.FINISH_LOADING_STATS,
  payload: {
    errorMessage,
    stats
  }
} as const);

type MyAppMetricsAction = ReturnType<typeof reset> | ReturnType<typeof finishLoadingDisksSizes> | ReturnType<typeof finishLoadingStats>;

const reducer = (state: MyAppMetricsState = INITIAL_STATE, action: MyAppMetricsAction): MyAppMetricsState => {
  switch (action.type) {
    case MyAppMetricsActionType.RESET:
      return INITIAL_STATE;
    
    case MyAppMetricsActionType.FINISH_LOADING_DISKS_SIZES: {
      let colors = (state.colors && { ...state.colors }) || undefined;

      let containerDiskSizeGraphProps: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max?: number } | undefined = undefined;
      let diskSizeGraphProps: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string } } | undefined = undefined;

      if (action.payload.disksSizes) {
        if (!colors) {
          colors = {};
        }

        const containerDiskSizeGraphData: { time: number, [containerId: string]: number }[] = [];
        const containerDiskSizeGraphColors: { [containerId: string]: string } = {};
        let containerDiskSizeGraphMax;

        const diskSizeGraphData: { time: number, [containerId: string]: number }[] = [];
        const diskSizeGraphColors: { [containerId: string]: string } = {};

        for (let i = 0; i < action.payload.disksSizes.length; i++) {
          const containerId = action.payload.disksSizes[i].container.id.split('-')[0];

          if (!colors[containerId]) {
            colors[containerId] = createRandomColor();
          }

          const containerMaxDiskSize = typeof action.payload.disksSizes[i].container.maxDiskSize === 'number' && (action.payload.disksSizes[i].container.maxDiskSize as number) * 1000 * 1000;

          containerDiskSizeGraphData.push({
            time: action.payload.disksSizes[i].time.getTime(),
            [containerId]: typeof containerMaxDiskSize === 'number' && action.payload.disksSizes[i].containerSize >= containerMaxDiskSize ? containerMaxDiskSize : action.payload.disksSizes[i].containerSize
          });

          if (!containerDiskSizeGraphColors[containerId]) {
            containerDiskSizeGraphColors[containerId] = colors[containerId];
          }

          if (typeof containerMaxDiskSize === 'number' && (typeof containerDiskSizeGraphMax !== 'number' || containerMaxDiskSize > (containerDiskSizeGraphMax as number))) {
            containerDiskSizeGraphMax = containerMaxDiskSize;
          }

          diskSizeGraphData.push({
            time: action.payload.disksSizes[i].time.getTime(),
            [containerId]: action.payload.disksSizes[i].size
          });

          if (!diskSizeGraphColors[containerId]) {
            diskSizeGraphColors[containerId] = colors[containerId];
          }
        }

        containerDiskSizeGraphProps = { data: containerDiskSizeGraphData, colors: containerDiskSizeGraphColors, max: containerDiskSizeGraphMax };
        diskSizeGraphProps = { data: diskSizeGraphData, colors: diskSizeGraphColors };
      }

      return {
        ...state,
        isLoadingDisksSizes: false,
        loadingDisksSizesErrorMessage: action.payload.errorMessage,
        colors,
        containerDiskSizeGraphProps,
        diskSizeGraphProps
      };
    }

    case MyAppMetricsActionType.FINISH_LOADING_STATS: {
      // A reload that failed, over charts that are already drawn: keep them.
      // The subscription reloads on its own within seconds; the error is for
      // when there is nothing to show instead. Same rule as the Overview.
      if (action.payload.errorMessage && state.cpuGraphProps && !state.loadingStatsErrorMessage) {
        return { ...state, isLoadingStats: false };
      }

      const { colors,
        cpuGraphProps,
        ramGraphProps,
        pidGraphProps,
        netInGraphProps,
        netOutGraphProps
      } = getContainerStatsGraphData(action.payload.stats || [], state.colors)

      return {
        ...state,
        isLoadingStats: false,
        loadingStatsErrorMessage: action.payload.errorMessage,
        colors,
        cpuGraphProps,
        ramGraphProps,
        pidGraphProps,
        netInGraphProps,
        netOutGraphProps
      };
    }
  }
};

const MyAppMetrics = () => {
  const { app } = useOutletContext<{ app: App }>();
  const appId = app && app.id;
  const mainServiceEnvironment = app && app.mainService && app.mainService.mainServiceEnvironment;
  const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  // Plain state rather than the reducer: the reducer exists to fold two live
  // subscriptions into per-container series, and this is one query that maps
  // one-to-one onto its chart.
  const [storageUsage, setStorageUsage] = useState<AppDatabaseUsage>({ points: [], quotaBytes: 0, packs: 0, status: 'OK' });
  let { isLoadingDisksSizes, loadingDisksSizesErrorMessage, containerDiskSizeGraphProps, diskSizeGraphProps, isLoadingStats, loadingStatsErrorMessage, cpuGraphProps, ramGraphProps, pidGraphProps, netInGraphProps, netOutGraphProps } = state;

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_APP_METRICS_PAGE);
  }, []);

  // Reads our own rows only — never the app's database — so it answers for an
  // app that is stopped, and for one whose database is unreachable. An app that
  // never bought the extra gets an empty series and no card, which is why there
  // is no error state here: there is nothing to report to an app that has no
  // database, and a failed request is a card that does not appear.
  useEffect(
    () => {
      if (!appId) {
        return;
      }

      let cancelled = false;

      back4app2
        .appDatabaseUsage(appId, STORAGE_DAYS)
        .then(usage => {
          if (!cancelled) {
            setStorageUsage(usage);
          }
        })
        .catch(error => {
          console.error('unexpected error loading app database usage', error);

          if (!cancelled) {
            setStorageUsage({ points: [], quotaBytes: 0, packs: 0, status: 'OK' });
          }
        });

      return () => { cancelled = true; };
    },
    [appId]
  );

  // One series, keyed by a name that is never drawn: with a single line the
  // chart uses the metric's own colour and the key only groups the points.
  const storageGraphData = storageUsage.points.map(point => ({
    time: new Date(point.time).getTime(),
    database: point.sizeBytes
  }));

  // The quota is a sum over the app's ACTIVE packs, so both the line and the
  // figure beside the reading rise on their own the moment another pack is
  // bought — nothing here is per-pack, and nothing changes when the catalog
  // grows a second database extra.
  const storagePeak = storageGraphData.reduce((max, row) => Math.max(max, row.database), 0);

  // The line is drawn only once the app is within reach of it, because drawing
  // it always means scaling the axis to it always: a database using 1% of its
  // pack becomes a flat line on the baseline under an empty box, and the chart
  // stops showing the one thing it is for. Under a quarter full there is no
  // question of headroom to answer — the figure next to the reading says what
  // the limit is, and the curve keeps the card.
  const storageLimit = storageUsage.quotaBytes > 0
    && storagePeak >= storageUsage.quotaBytes * 0.25
    ? {
      value: storageUsage.quotaBytes,
      label: `${formatBytes(storageUsage.quotaBytes)} limit${storageUsage.packs > 1 ? ` · ${storageUsage.packs} packs` : ''}`
    }
    : undefined;

  useEffect(
    () => {
      let disksSizesSubscription: Subscription;
      let statsSubscription: Subscription;

      if (mainServiceEnvironmentId) {
        disksSizesSubscription = back4app2.subscribeToContainersDisksSizes(
          mainServiceEnvironmentId,
          STATS_WINDOW,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingDisksSizes('Network error when loading app disks sizes. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading app disks sizes', error);
                dispatch(finishLoadingDisksSizes('Unexpected error when loading app disks sizes. Please try again.'));
              }
            } else {
              dispatch(finishLoadingDisksSizes(undefined, snapshot));
            }
          }
        );

        statsSubscription = back4app2.subscribeToContainersStats(
          mainServiceEnvironmentId,
          STATS_WINDOW,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingStats('Network error when loading app stats. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading app stats', error);
                dispatch(finishLoadingStats('Unexpected error when loading app stats. Please try again.'));
              }
            } else {
              dispatch(finishLoadingStats(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (disksSizesSubscription) {
          disksSizesSubscription.unsubscribe();          
        }

        if (statsSubscription) {
          statsSubscription.unsubscribe();          
        }

        dispatch(reset());
      };
    },
    [
      mainServiceEnvironmentId
    ]
  );

  return (<>
    <ScreenHead title="Metrics" />

    {/* Three subjects, ruled apart, instead of eight charts in one run. They
        answer different questions — is it keeping up, is it talking, is it
        filling up — and reading them as one list meant finding the right chart
        by counting.

        The window moves to the group head wherever every card in the group
        shares one, which is where most of the repetition was. Storage keeps it
        per card, because its cards genuinely differ: two are live, the
        database is a daily sweep. */}
    <SettingsSection eyebrow="compute" meta={STATS_WINDOW_LABEL}>
      <div className="grid gap-4 lg:grid-cols-2">
        <MetricChart
          label="cpu"
          data={cpuGraphProps?.data}
          color="#4FA7EF"
          colors={cpuGraphProps?.colors}
          yMax={100}
          format={(value) => `${value.toFixed(0)}%`}
          isLoading={isLoadingStats && !!mainServiceEnvironmentId}
          errorMessage={loadingStatsErrorMessage}
        />
        <MetricChart
          label="ram"
          data={ramGraphProps?.data}
          color="#4AC77B"
          colors={ramGraphProps?.colors}
          yMax={ramGraphProps?.max}
          format={formatMB}
          unit="MB"
          isLoading={isLoadingStats && !!mainServiceEnvironmentId}
          errorMessage={loadingStatsErrorMessage}
        />
        <MetricChart
          label="processes"
          data={pidGraphProps?.data}
          color="#8FA2BD"
          colors={pidGraphProps?.colors}
          yMax={pidGraphProps?.max}
          format={(value) => value.toFixed(0)}
          isLoading={isLoadingStats && !!mainServiceEnvironmentId}
          errorMessage={loadingStatsErrorMessage}
        />
      </div>
    </SettingsSection>

    <SettingsSection eyebrow="network" meta={STATS_WINDOW_LABEL}>
      <div className="grid gap-4 lg:grid-cols-2">
        <MetricChart
          label="network in"
          data={netInGraphProps?.data}
          color="#4FA7EF"
          colors={netInGraphProps?.colors}
          format={formatRate}
          isLoading={isLoadingStats && !!mainServiceEnvironmentId}
          errorMessage={loadingStatsErrorMessage}
        />
        <MetricChart
          label="network out"
          data={netOutGraphProps?.data}
          color="#C94B8E"
          colors={netOutGraphProps?.colors}
          format={formatRate}
          isLoading={isLoadingStats && !!mainServiceEnvironmentId}
          errorMessage={loadingStatsErrorMessage}
        />
      </div>
    </SettingsSection>

    <SettingsSection eyebrow="storage">
      <div className="grid gap-4 lg:grid-cols-2">
        <MetricChart
          label={<>total disk usage <span className="text-navy-400">· {STATS_WINDOW_LABEL}</span></>}
          data={diskSizeGraphProps?.data}
          color="#F59E0B"
          colors={diskSizeGraphProps?.colors}
          format={formatBytes}
          isLoading={isLoadingDisksSizes && !!mainServiceEnvironmentId}
          errorMessage={loadingDisksSizesErrorMessage}
        />
        <MetricChart
          label={<>container disk usage <span className="text-navy-400">· {STATS_WINDOW_LABEL}</span></>}
          data={containerDiskSizeGraphProps?.data}
          color="#F59E0B"
          colors={containerDiskSizeGraphProps?.colors}
          yMax={containerDiskSizeGraphProps?.max}
          format={formatBytes}
          isLoading={isLoadingDisksSizes && !!mainServiceEnvironmentId}
          errorMessage={loadingDisksSizesErrorMessage}
        />
        {/* Only for an app that has one. Most apps never bought the database
            extra, and a permanently empty card on everyone's Metrics reads as
            a metric that is broken rather than one that does not apply.

            The quota is always stated beside the reading and becomes a line
            across the plot once the app is near it — see `storageLimit`. */}
        {storageGraphData.length > 0 ? (
          <MetricChart
            label={<>database storage <span className="text-navy-400">· {STORAGE_WINDOW_LABEL}</span></>}
            data={storageGraphData}
            color="#A78BFA"
            format={formatBytes}
            formatTime={dayLabel}
            unit={storageUsage.quotaBytes > 0 ? `of ${formatBytes(storageUsage.quotaBytes)}` : undefined}
            reference={storageLimit}
          />
        ) : null}
      </div>
    </SettingsSection>
  </>);
};

export default MyAppMetrics;
